import * as Yup from 'yup';

import {
  FormField,
  InputOptions,
  RadioOptions,
  FormFieldType,
  SelectOptions,
  FormSubmissionData,
  ScaleRatingOptions,
  OutcomesFieldValue,
  OutcomesFieldOption,
} from 'types';

import { passwordPattern } from './regex';

export const advancedPassword = Yup.string()
  .trim()
  .matches(
    passwordPattern,
    'Password should contain minimum 8 characters with one numeric, one lowercase and one uppercase character'
  )
  .required();

export const generateFormFieldsValidationSchema = (
  fields: FormField[],
  submissionData: FormSubmissionData | null = null
): Yup.ObjectSchema<any> => {
  let schemaFields: Record<string, Yup.AnySchema> = {};

  fields.forEach((field) => {
    schemaFields = createFieldValidation(field, schemaFields, submissionData);
  });

  return Yup.object().shape(schemaFields);
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isOutcomeOptionArray = (value: unknown): value is OutcomesFieldOption[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      typeof item.id === 'string' &&
      typeof item.label === 'string' &&
      (item.description === undefined || typeof item.description === 'string')
  );

export const normalizeOutcomesFieldValue = (
  value: unknown
): OutcomesFieldValue => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {
      outcomes: [],
      orderedIds: [],
    };
  }

  const outcomesValue = value as Record<string, unknown>;

  return {
    outcomes: isOutcomeOptionArray(outcomesValue.outcomes)
      ? outcomesValue.outcomes
      : [],
    orderedIds: isStringArray(outcomesValue.orderedIds)
      ? outcomesValue.orderedIds
      : isStringArray(outcomesValue.prioritizedOutcomeIds)
        ? outcomesValue.prioritizedOutcomeIds
        : [],
  };
};

export const buildOrderedOutcomes = (
  outcomes: OutcomesFieldOption[],
  orderedIds: string[]
): OutcomesFieldOption[] => {
  if (!outcomes.length) {
    return [];
  }

  const outcomesById = new Map(outcomes.map((item) => [item.id, item]));
  const orderedOutcomes = orderedIds
    .map((id) => outcomesById.get(id))
    .filter((item): item is OutcomesFieldOption => Boolean(item));
  const orderedOutcomeIds = new Set(orderedOutcomes.map((item) => item.id));

  return [
    ...orderedOutcomes,
    ...outcomes.filter((item) => !orderedOutcomeIds.has(item.id)),
  ];
};

const createFieldValidation = (
  field: FormField,
  schemaFields: Record<string, Yup.AnySchema>,
  submissionData: FormSubmissionData | null
): Record<string, Yup.AnySchema> => {
  if (field.type === FormFieldType.FIELD_ARRAY) {
    let schema = {
      ...schemaFields,
    };
    field.options?.fields.forEach((field) => {
      schema = createFieldValidation(field, schema, submissionData);
    });
    return schema;
  }

  let validation: Yup.AnySchema;

  switch (field.type) {
    case FormFieldType.INPUT:
      validation = createInputValidation(field.options as InputOptions);
      break;

    case FormFieldType.SELECT:
      validation = createSelectValidation(field.options as SelectOptions);
      break;

    case FormFieldType.RADIO:
      validation = createRadioValidation(field.options as RadioOptions);
      break;

    case FormFieldType.SCALE_RATING:
      validation = createScaleRatingValidation(
        field.options as ScaleRatingOptions,
        field.required
      );
      break;

    case FormFieldType.OUTCOMES:
      validation = createOutcomesValidation();
      break;

    case FormFieldType.OUTCOME_FIELDS:
      validation = createOutcomeFieldsValidation(
        field,
        submissionData,
        field.required
      );
      break;

    case FormFieldType.PAGE_BREAK:
      validation = Yup.object();
      break;
  }

  if (field.type !== FormFieldType.OUTCOME_FIELDS) {
    if (field.required) {
      validation = validation.required('Field is required');
    } else {
      validation = validation.nullable().optional();
    }
  }

  return {
    ...schemaFields,
    [field.id]: validation,
  };
};

const createInputValidation = (options?: InputOptions): Yup.AnySchema => {
  if (!options) {
    return Yup.string();
  }

  let validation: Yup.StringSchema | Yup.NumberSchema;

  if (options.type === 'date') {
    return Yup.date()
      .nullable()
      .transform((_value, originalValue) =>
        originalValue == null
          ? null
          : (originalValue?.toDate?.() ?? originalValue)
      )
      .typeError('Must be a valid date');
  }

  if (options.type === 'number') {
    validation = Yup.number().typeError('Must be a number');

    if (options.minValue !== undefined) {
      validation = validation.min(
        options.minValue,
        `Must be at least ${options.minValue}`
      );
    }

    if (options.maxValue !== undefined) {
      validation = validation.max(
        options.maxValue,
        `Must be at most ${options.maxValue}`
      );
    }

    if (options.integer) {
      validation = validation.integer('Must be an integer');
    }
  } else {
    validation = Yup.string();

    if (options.type === 'email') {
      validation = validation.email('Must be a valid email address');
    }

    if (options.minLength !== undefined) {
      validation = validation.min(
        options.minLength,
        `Must be at least ${options.minLength} characters`
      );
    }

    if (options.maxLength !== undefined) {
      validation = validation.max(
        options.maxLength,
        `Must be at most ${options.maxLength} characters`
      );
    }
  }

  return validation;
};

const createSelectValidation = (options?: SelectOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.string();
  }
  if (options.multiple) {
    return Yup.array()
      .of(Yup.string().oneOf(options.options, 'Invalid option selected'))
      .min(1, 'At least one option must be selected');
  } else {
    return Yup.string().oneOf(
      options.options,
      `Must be one of: ${options.options.join(', ')}`
    );
  }
};

const createRadioValidation = (options?: RadioOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.string();
  }
  return Yup.string().oneOf(
    options.options,
    `Must be one of: ${options.options.join(', ')}`
  );
};

const createScaleRatingValidation = (
  options?: ScaleRatingOptions,
  isRequired?: boolean
): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options) || !options.options.length) {
    return Yup.object();
  }
  const { minValue, maxValue } = options;

  return Yup.array().of(
    Yup.number()
      .min(minValue, `Rating must be at least ${minValue}`)
      .max(maxValue, `Rating must be at most ${maxValue}`)
      .when([], {
        is: () => isRequired,
        otherwise: (schema) => schema.nullable().optional(),
        then: (schema) => schema.required(`Rating is required`),
      })
  );
};

const createOutcomesValidation = (): Yup.AnySchema =>
  Yup.object({
    orderedIds: Yup.array().of(Yup.string().required()).required(),
    outcomes: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required(),
          label: Yup.string().required(),
          description: Yup.string().optional(),
        }) as Yup.ObjectSchema<OutcomesFieldOption>
      )
      .optional(),
  });

const createOutcomeFieldsValidation = (
  field: Extract<FormField, { type: FormFieldType.OUTCOME_FIELDS }>,
  submissionData: FormSubmissionData | null,
  isRequired?: boolean
): Yup.AnySchema => {
  const nestedSchema = generateFormFieldsValidationSchema(
    field.options?.fields ?? [],
    null
  );

  // Use Yup.lazy so that the schema is built from the actual runtime value keys
  // rather than the snapshot of submissionData passed at initialization. This
  // prevents validation mismatches when the currentOutcomeFields API returns a
  // different set of outcome IDs than what was in the stored snapshot.
  return Yup.lazy((value: unknown) => {
    if (value == null) {
      return isRequired
        ? Yup.mixed().required('Field is required')
        : Yup.mixed().nullable().optional();
    }

    if (typeof value !== 'object') {
      return Yup.object();
    }

    return Yup.object().shape(
      Object.fromEntries(
        Object.keys(value as Record<string, unknown>).map((key) => [
          key,
          nestedSchema,
        ])
      )
    );
  }) as unknown as Yup.AnySchema;
};
